// skills/pyre/pyreData.js
// Pyre logs and configuration

// Base XP reduced ~35% from original values to make rituals the primary progression path
// Original values: 40, 60, 90, 135, 202.5, 303.8
// Each log has a tier (1-6) that determines which ritual can be used with it
export const LOGS = {
    COMMON_TIMBER: {
        id: 'common_timber_pyre',
        name: 'Common Timber',
        level: 1,
        tier: 1,
        xp: 26,
        baseTime: 2000,
        inputItem: 'common_timber',
        inputQuantity: 1,
        drops: [
            { itemId: 'ashes', weight: 1, quantity: 1 }
        ],
        color: '#8b4513'
    },
    IRONWOOD: {
        id: 'ironwood_pyre',
        name: 'Ironwood Logs',
        level: 15,
        tier: 2,
        xp: 39,
        baseTime: 2500,
        inputItem: 'ironwood_logs',
        inputQuantity: 1,
        drops: [
            { itemId: 'ashes', weight: 1, quantity: 1 }
        ],
        color: '#a0522d'
    },
    SOFTBARK: {
        id: 'softbark_pyre',
        name: 'Softbark Logs',
        level: 30,
        tier: 3,
        xp: 59,
        baseTime: 3000,
        inputItem: 'softbark_logs',
        inputQuantity: 1,
        drops: [
            { itemId: 'ashes', weight: 1, quantity: 1 }
        ],
        color: '#9acd32'
    },
    GOLDLEAF: {
        id: 'goldleaf_pyre',
        name: 'Goldleaf Logs',
        level: 45,
        tier: 4,
        xp: 88,
        baseTime: 3500,
        inputItem: 'goldleaf_logs',
        inputQuantity: 1,
        drops: [
            { itemId: 'ashes', weight: 1, quantity: 1 }
        ],
        color: '#ff8c00'
    },
    GRAVEWOOD: {
        id: 'gravewood_pyre',
        name: 'Gravewood Logs',
        level: 60,
        tier: 5,
        xp: 132,
        baseTime: 4000,
        inputItem: 'gravewood_logs',
        inputQuantity: 1,
        drops: [
            { itemId: 'ashes', weight: 1, quantity: 1 }
        ],
        color: '#228b22'
    },
    ARCANEWOOD: {
        id: 'arcanewood_pyre',
        name: 'Arcanewood Logs',
        level: 75,
        tier: 6,
        xp: 197,
        baseTime: 5000,
        inputItem: 'arcanewood_logs',
        inputQuantity: 1,
        drops: [
            { itemId: 'ashes', weight: 1, quantity: 1 }
        ],
        color: '#9370db'
    }
};

/**
 * Ritual definitions - enhanced pyre actions using bones
 * Each ritual tier unlocks at a specific Pyre level
 * Rituals consume log + bone for multiplied XP
 * Each ritual is restricted to specific log tiers
 */
export const RITUALS = {
    MINOR: {
        id: 'minor_ritual',
        name: 'Minor Ritual',
        level: 10,
        boneId: 'small_bone',
        boneName: 'Small Bone',
        xpMultiplier: 1.5,
        allowedLogTiers: [1, 2],  // Common Timber, Ironwood
        color: '#e8dcc8',
        description: 'A simple offering that stokes the flames.'
    },
    TEMPERED: {
        id: 'tempered_ritual',
        name: 'Tempered Ritual',
        level: 25,
        boneId: 'charred_bone',
        boneName: 'Charred Bone',
        xpMultiplier: 1.75,
        allowedLogTiers: [3],  // Softbark
        color: '#4a4a4a',
        description: 'Fire-touched bones burn with renewed intensity.'
    },
    GREATER: {
        id: 'greater_ritual',
        name: 'Greater Ritual',
        level: 45,
        boneId: 'dragon_bone',
        boneName: 'Dragon Bone',
        xpMultiplier: 2.1,
        allowedLogTiers: [4],  // Goldleaf
        color: '#8b0000',
        description: 'Draconic essence amplifies the pyre\'s power.'
    },
    VOIDBOUND: {
        id: 'voidbound_ritual',
        name: 'Voidbound Ritual',
        level: 65,
        boneId: 'void_bone',
        boneName: 'Void Bone',
        xpMultiplier: 2.5,
        allowedLogTiers: [5],  // Gravewood
        color: '#4b0082',
        description: 'Void-touched remnants warp the flames beyond mortal limits.'
    },
    ELDER: {
        id: 'elder_ritual',
        name: 'Elder Ritual',
        level: 80,
        boneId: 'elder_bone',
        boneName: 'Elder Bone',
        xpMultiplier: 3.0,
        allowedLogTiers: [6],  // Arcanewood
        color: '#daa520',
        description: 'Ancient bones channel primordial fire.'
    }
};

/**
 * Get ritual by bone ID
 */
export function getRitualByBone(boneId) {
    return Object.values(RITUALS).find(r => r.boneId === boneId) || null;
}

/**
 * Get available rituals for a given Pyre level
 */
export function getAvailableRituals(pyreLevel) {
    return Object.values(RITUALS).filter(r => pyreLevel >= r.level);
}

/**
 * Get logs that can be used with a specific ritual
 */
export function getLogsForRitual(ritual) {
    if (!ritual || !ritual.allowedLogTiers) return [];
    return Object.values(LOGS).filter(log => ritual.allowedLogTiers.includes(log.tier));
}

/**
 * Check if a log can be used with a ritual
 */
export function canLogBeUsedInRitual(log, ritual) {
    if (!log || !ritual || !ritual.allowedLogTiers) return false;
    return ritual.allowedLogTiers.includes(log.tier);
}

/**
 * Get ashes output for a ritual based on log tier
 * Higher tier logs produce more ashes when used in rituals
 * Returns: Tier 1→2, Tier 2→3, Tier 3→4, Tier 4→5, Tier 5→6, Tier 6→10
 */
export function getRitualAshesOutput(logTier) {
    if (logTier === 6) return 10;  // Arcanewood + Elder Bone = max ashes
    return logTier + 1;  // Tiers 1-5: tier + 1
}

export const TOOLS = {
    TINDERBOX: {
        id: 'tinderbox',
        name: 'Tinderbox',
        level: 1,
        speedMultiplier: 1.0,
        price: 0
    },
    TORCH: {
        id: 'torch',
        name: 'Torch',
        level: 15,
        speedMultiplier: 1.2,
        price: 1000
    },
    LANTERN: {
        id: 'lantern',
        name: 'Lantern',
        level: 30,
        speedMultiplier: 1.4,
        price: 5000
    },
    FIRE_STAFF: {
        id: 'fire_staff',
        name: 'Fire Staff',
        level: 45,
        speedMultiplier: 1.6,
        price: 25000
    },
    EMBER_BRAZIER: {
        id: 'ember_brazier',
        name: 'Ember Brazier',
        level: 60,
        speedMultiplier: 1.8,
        price: 100000
    },
    ARCANE_TORCH: {
        id: 'arcane_torch',
        name: 'Arcane Torch',
        level: 75,
        speedMultiplier: 2.0,
        price: 250000
    }
};

export default {
    LOGS,
    RITUALS,
    TOOLS,
    getRitualByBone,
    getAvailableRituals,
    getLogsForRitual,
    canLogBeUsedInRitual,
    getRitualAshesOutput
};
